import React from 'react';
import { BASEURL } from '@services/network';
import { logError, logger } from '@utils/usefulFuncs';
import { runQualityCheck } from '@services/application';
import { logError as logSentryError } from '@utils/sentry';
import { createAuthedEventSource, getStreamAuthHeaders } from '@utils/streamAuth';

// Use the standardized error logging utility instead of direct Sentry calls
const logStreamingError = (error, message) => {
  logSentryError(new Error('Error streaming answer'), {
    error: error.toString(),
    message: message,
    component: 'useStreamingResult',
  });
};

export const runStream = (URL, handlers = {}, options) => {
  return new Promise((resolve, reject) => {
    const { onOpen, onMessage, onRefiningStart, onError, onComplete } = handlers;

    const source = createAuthedEventSource(URL, { headers: options?.headers });
    source.addEventListener('open', function (event) {
      try {
        onOpen && onOpen();
      } catch (e) {
        logError('error occurred in stream', e);
        reject('error occurred in stream');
      }
    });
    source.addEventListener('error', function (error) {
      try {
        const READY_STATE = { 0: 'CONNECTING', 1: 'OPEN', 2: 'CLOSED' };
        console.error('[runStream] stream error event', {
          url: URL,
          readyState: source.readyState,
          readyStateName: READY_STATE[source.readyState],
          status: error?.status ?? error?.statusCode,
          statusText: error?.statusText,
          message: error?.message,
          hasAuthHeader: !!options?.headers?.Authorization,
          eventType: error?.type,
        });
        onError && onError(error);
        source.close();
      } catch (e) {
        source.close();
        logError('error occurred in stream', e);
        reject('error occurred in stream');
      }
    });
    source.addEventListener('message', function (event) {
      try {
        const { data } = event;
        let parsed = null;
        try {
          parsed = JSON.parse(data);
        } catch {
          // Stream events can be non-JSON while the UI still passes the raw payload below.
        }
        logger('runStream onMessage', 'received', {
          url: URL,
          questionIndex: parsed?.question_index,
          finalAnswer: parsed?.final_answer === true,
          answerLength: parsed?.answer?.length || 0,
        });
        onMessage && onMessage(data);
      } catch (e) {
        logError('error occurred in stream', e);
        reject('error occurred in stream');
      }
    });
    source.addEventListener('complete', function (event) {
      try {
        try {
          console.log('runStream complete payload', JSON.parse(event.data));
        } catch {
          console.log('runStream complete payload (raw)', event.data);
        }
        onComplete && onComplete(event);
        resolve(event);
        source.close();
      } catch (e) {
        logError('error occurred in stream', e);
        reject('error occurred in stream');
      }
    });
  });
};

export function streamResult(resultId, handlers = {}, options) {
  let source = null;

  const promise = new Promise((resolve, reject) => {
    const { onOpen, onMessage, onRefiningStart, onError, onComplete } = handlers;

    const params = [`run_id=${encodeURIComponent(resultId)}`, 'include_context=true'];
    if (options?.includeAgentPlan) params.push('include_agent_plan=true');
    const URL = `${BASEURL}/api/v2/genai/queries/stream_events?${params.join('&')}`;
    source = createAuthedEventSource(URL, { headers: options?.headers });
    source.addEventListener('open', function (event) {
      try {
        onOpen && onOpen();
      } catch (e) {
        logError('error occurred in stream', e);
        reject('error occurred in stream');
      }
    });
    source.addEventListener('error', function (error) {
      try {
        const READY_STATE = { 0: 'CONNECTING', 1: 'OPEN', 2: 'CLOSED' };
        console.error('[streamResult] stream error event', {
          url: URL,
          readyState: source.readyState,
          readyStateName: READY_STATE[source.readyState],
          status: error?.status ?? error?.statusCode,
          statusText: error?.statusText,
          message: error?.message,
          hasAuthHeader: !!getStreamAuthHeaders().Authorization,
          eventType: error?.type,
        });
        logStreamingError(error, error?.message);
        onError && onError(error);
        source.close();
        reject('error occurred in stream');
      } catch (e) {
        logError('error occurred in stream', e);
        reject('error occurred in stream');
      }
    });
    source.addEventListener('message', function (event) {
      try {
        const { data } = event;
        const streamData = JSON.parse(data);
        const { answer } = streamData;
        logger('streamResult onMessage', 'received', {
          runId: resultId,
          questionIndex: streamData?.question_index,
          finalAnswer: streamData?.final_answer === true,
          answerLength: answer?.length || 0,
        });
        if (answer?.length > 0) {
          onMessage && onMessage(answer);
        }
      } catch (e) {
        logError('error occurred in stream', e);
        reject('error occurred in stream');
      }
    });
    source.addEventListener('refining', function (event) {
      try {
        onRefiningStart && onRefiningStart();
      } catch (e) {
        logError('error occurred in stream', e);
        reject('error occurred in stream');
      }
    });
    source.addEventListener('complete', function (event) {
      try {
        const { data } = event;
        const streamData = JSON.parse(data);
        console.log('streamResult complete payload', streamData);
        const { response } = streamData;
        const answers = response?.answers || [];
        const row = answers.find((r) => r?.final_answer) || answers[0];
        if (row?.answer?.length > 0) {
          const result = {
            answer: row.answer,
            queryRunId: row.query_run_id,
            kind: row.kind,
            context: row.context,
            fullContext: row.context_with_all_headers,
            sources: row.sources,
          };
          if (row.kind === 'agent' && row.agent_executed_plan) {
            result.agentExecutedPlan = row.agent_executed_plan;
          }
          onComplete(result, response);
          if (options?.enableQualityCheck && row.query_run_id) {
            runQualityCheck(row.query_run_id);
          }
          resolve(result);
        }
      } catch (e) {
        logError('error occurred in stream', e);
        reject('error occurred in stream');
      }
      source.close();
    });
  });

  return {
    promise,
    cancel: () => {
      if (source) {
        source.close();
      }
    },
  };
}

export const useStreamingResult = (resultId, options = {}) => {
  const [result, setResult] = React.useState('');

  const [refiningMessage, setRefiningMessage] = React.useState(false);
  const [fullContext, setFullContext] = React.useState(null);
  const [context, setContext] = React.useState(null);
  const [sources, setSources] = React.useState(null);
  const [queryRunId, setQueryRunId] = React.useState(null);
  const [streamEnd, setStreamEnd] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [reOpenStream, setReOpenStream] = React.useState(false);

  const getStreamingResponse = async (resultId, status) => {
    if (!resultId) {
      return null;
    }

    function onOpen() {
      if (!status.isCanceled) {
        setResult('Working...');
        setContext(null);
        setSources(null);
        setQueryRunId(null);
        setStreamEnd(false);
      }
    }

    function onMessage(answer) {
      if (!status.isCanceled) {
        if (answer?.length > 0) {
          if (!status.isCanceled) {
            setResult(answer);
            setError(null);
          }
        }
      }
    }

    function onRefiningStart() {
      if (!status.isCanceled) {
        setRefiningMessage(true);
      }
    }

    function onError(error) {
      if (!status.isCanceled) {
        console.log('error occurred in stream', error, error?.currentTarget, error?.currentTarget?.message);
        setError('Something went wrong.');
        logSentryError(error, error?.message);
        setTimeout(() => {
          setReOpenStream(true);
        }, 1000);
      }
    }

    function onComplete(result) {
      const { answer, queryRunId, context, fullContext, sources } = result;
      if (!status.isCanceled) {
        setResult(answer);
        setRefiningMessage(false);
        setContext(context);
        setSources(sources);
        setQueryRunId(queryRunId);
        setError(null);
        setFullContext(fullContext);
        setStreamEnd(true);
      }
    }

    const handlers = {
      onOpen,
      onMessage,
      onRefiningStart,
      onError,
      onComplete,
    };
    return streamResult(resultId, handlers, options);
  };

  React.useEffect(() => {
    if (resultId !== null) {
      let status = { isCanceled: false };
      const streamSource = getStreamingResponse(resultId, status);
      return () => {
        try {
          status.isCanceled = true;
          if (streamSource && streamSource.cancel) {
            streamSource.cancel();
          }
        } catch (e) {
          console.error(e);
        }
      };
    }
  }, [resultId]);

  React.useEffect(() => {
    if (resultId !== null && reOpenStream) {
      let status = { isCanceled: false };
      const streamSource = getStreamingResponse(resultId, status);
      return () => {
        try {
          status.isCanceled = true;
          if (streamSource && streamSource.cancel) {
            streamSource.cancel();
          }
        } catch (e) {
          console.error(e);
        }
      };
    }
  }, [reOpenStream]);

  return {
    result,
    isRefiningAnswer: refiningMessage,
    context,
    sources,
    fullContext,
    queryRunId,
    error,
    streamEnd,
  };
};
